import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	Modal,
	Select,
	Segmented,
	Space,
	Steps,
	Tag,
	Typography,
} from 'antd';
import {
	ArrowLeftOutlined,
	ArrowRightOutlined,
	CheckCircleOutlined,
	FileSyncOutlined,
	PlusCircleOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import dayjs from 'dayjs';
import { createPatient, fetchPatients, fetchSinglePatientByUuid } from '../../../../api/healthcare/PatientApi';
import {
	extractIhiValidationFromMutationError,
	parseIhiValidationFeedback,
} from '../../../../utils/healthcare/IhiValidationFeedback';
import { validateIHI } from '../../../../utils/healthcare/ValidateIhiNumber';
import { assignPlan, fetchTemplates } from '../../../../api/healthcare/TreatmentPlanApi';
import { TreatmentPlanTemplate } from '../../../../types/healthcare/TreatmentPlanTypes';
import { Patient } from '../../../../types/healthcare/PatientTypes';
import { useNotifications } from '../../../../context/NotificationContext';
import MedicalDirectorImportModal from '../../patient/MedicalDirectorImportModal';
import { XmlImportJobStatus } from '../../../../api/healthcare/PatientApi';
import { BetaNotice } from '../../../../components/shared';
import { generatePassword } from '../../../../utils/generatePassword';
import { sendCommunication } from '../../../../api/communications/communicationApi';
import { SendCommunicationRequest } from '../../../../types/communications/CommunicationTypes';
import { ENV } from '../../../../config/runtimeEnv';
import type { UserUuid } from '../../../../types/identity';

const { Text, Title } = Typography;

export const LOGIN_DETAILS_SEND_LABEL = 'Send login details to the patient';
export const LOGIN_DETAILS_NOT_SENT_TITLE = 'Login details not sent';
export const LOGIN_DETAILS_NOT_SENT_MESSAGE =
	'The patient record and treatment plan were created, but the login details could not be sent. ' +
	'Resend them from the patient record, or ask the patient to use the reset-password link on the login page.';
export const IHI_NOT_SAVED_TITLE = 'IHI not saved';
export const IHI_SAVED_TITLE = 'IHI saved';
export const TREATMENT_PLAN_CREATED_MESSAGE = 'Treatment plan created successfully';
export const PATIENT_NOT_SAVED_TITLE = 'Patient not saved';
export const PATIENT_NOT_SAVED_UNCONFIRMED_MESSAGE =
	'The patient record was not saved, and the outcome of the supplied IHI could not be confirmed. ' +
	'Check the entered details and try again, or create the patient without an IHI.';
export const PATIENT_ALREADY_CREATED_TITLE = 'Patient record already created';
export const PATIENT_ALREADY_CREATED_MESSAGE =
	'The patient record was created and has been kept. Only the treatment-plan assignment is ' +
	'outstanding, and retrying will assign the plan to that record. The patient details can no ' +
	'longer be changed here - edit them from the patient record instead.';


const responseCarriesIhiValidation = (error: unknown): boolean => {
	if (typeof error !== 'object' || error === null) return false;

	const response = (error as { response?: unknown }).response;
	if (typeof response !== 'object' || response === null) return false;

	const data = (response as { data?: unknown }).data;
	if (typeof data !== 'object' || data === null) return false;

	return Object.prototype.hasOwnProperty.call(data, 'ihi_validation');
};

// CU-14ygtkj65jy — the field is optional, so only a supplied candidate is checked,
// through the existing validateIHI contract rather than a second local policy.
const validateIhiCandidate = (_rule: unknown, value: unknown) => {
	if (typeof value !== 'string' || value === '') return Promise.resolve();
	const result = validateIHI(value);
	return result.isValid
		? Promise.resolve()
		: Promise.reject(new Error(result.error || 'Invalid IHI number.'));
};

interface Props {
	open: boolean;
	onClose: () => void;
	onCreated: () => void;
	/** When set, pre-selects the first template matching this string and skips to step 1 */
	initialPathway?: string;
}

type PatientMode = 'new' | 'existing';

const GENDER_OPTIONS = [
	{ value: 'Male', label: 'Male' },
	{ value: 'Female', label: 'Female' },
	{ value: 'Other', label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
	Peptides:    '#7c3aed',
	Genetics:    '#0d9488',
	Onboarding:  '#2563eb',
	Wellness:    '#16a34a',
	Supplements: '#d97706',
};

const TemplateCard: React.FC<{
	template: TreatmentPlanTemplate;
	selected: boolean;
	onSelect: () => void;
}> = ({ template, selected, onSelect }) => {
	const accent = CATEGORY_COLORS[template.category ?? ''] ?? '#7c3aed';
	return (
		<div
			onClick={onSelect}
			style={{
				padding: '14px 16px',
				borderRadius: 10,
				border: `2px solid ${selected ? accent : 'var(--mg-border)'}`,
				background: selected ? `${accent}0d` : 'var(--mg-bg-elevated)',
				cursor: 'pointer',
				transition: 'all 0.15s',
				boxShadow: selected ? `0 0 0 3px ${accent}22` : '0 1px 3px rgba(0,0,0,.04)',
				display: 'flex',
				flexDirection: 'column',
				gap: 6,
				minHeight: 88,
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
				<Text strong style={{ fontSize: 13, lineHeight: 1.3, flex: 1 }}>{template.name}</Text>
				{selected && <CheckCircleOutlined style={{ color: accent, fontSize: 15, flexShrink: 0 }} />}
			</div>
			{template.category && (
				<Tag
					style={{
						alignSelf: 'flex-start',
						background: `${accent}18`,
						color: accent,
						border: `1px solid ${accent}44`,
						borderRadius: 20,
						fontSize: 10,
						fontWeight: 600,
						padding: '0 8px',
						lineHeight: '18px',
					}}
				>
					{template.category}
				</Tag>
			)}
			{template.description && (
				<Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4 }}>
					{template.description}
				</Text>
			)}
		</div>
	);
};

const CreateTreatmentPlanModal: React.FC<Props> = ({ open, onClose, onCreated, initialPathway }) => {
	const [form] = Form.useForm();
	const [submitting, setSubmitting] = useState(false);
	const [step, setStep] = useState(0);
	const [mode, setMode] = useState<PatientMode>('new');

	const [templates, setTemplates] = useState<TreatmentPlanTemplate[]>([]);
	const [loadingTemplates, setLoadingTemplates] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<TreatmentPlanTemplate | null>(null);

	const [patientOptions, setPatientOptions] = useState<{ value: UserUuid; label: string; email?: string }[]>([]);
	const [searchingPatients, setSearchingPatients] = useState(false);
	const [selectedExistingPatient, setSelectedExistingPatient] = useState<{ id: UserUuid; name: string; email?: string } | null>(null);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [importModalOpen, setImportModalOpen] = useState(false);

	const [sendLoginDetails, setSendLoginDetails] = useState(true);

	const createdPatientRef = useRef<Patient | null>(null);
	const [patientCreated, setPatientCreated] = useState(false);

	const { showError, showSuccess, showWarning } = useNotifications();
	const user = getSessionUser();

	const loadTemplates = useCallback(async () => {
		setLoadingTemplates(true);
		try {
			const data = await fetchTemplates();
			const active = data.filter((t) => t.is_active);
			setTemplates(active);
			if (initialPathway) {
				const match = active.find((t) =>
					t.name.toLowerCase().includes(initialPathway.toLowerCase())
				);
				if (match) {
					setSelectedTemplate(match);
					setStep(1);
				}
			}
		} catch {
			// non-critical
		} finally {
			setLoadingTemplates(false);
		}
	}, [initialPathway]);

	useEffect(() => {
		if (open) {
			form.resetFields();
			setStep(0);
			setMode('new');
			setSelectedTemplate(null);
			setSelectedExistingPatient(null);
			setPatientOptions([]);
			setImportModalOpen(false);
			setSendLoginDetails(true);
			createdPatientRef.current = null;
			setPatientCreated(false);
			loadTemplates();
		}
	}, [open]);

	const handlePatientSearch = useCallback((value: string) => {
		if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		if (!value.trim()) {
			setPatientOptions([]);
			return;
		}
		searchTimeoutRef.current = setTimeout(async () => {
			setSearchingPatients(true);
			try {
				const result = await fetchPatients(1, 20, value);
				setPatientOptions(
					result.data.map((p) => ({
						value: p.id,
						label: `${p.first_name} ${p.last_name} — ${p.email}${p.phone ? ` — ${p.phone}` : ""}`,
						email: p.email,
					}))
				);
			} catch {
				// non-critical
			} finally {
				setSearchingPatients(false);
			}
		}, 300);
	}, []);

	const handleModeChange = (val: string | number) => {
		setMode(val as PatientMode);
		setSelectedExistingPatient(null);
		form.resetFields(['existing_patient_id', 'first_name', 'last_name', 'email', 'phone', 'date_of_birth', 'gender', 'ihi_number']);
		setPatientOptions([]);
	};

	const goNext = async () => {
		if (step === 0) {
			if (!selectedTemplate) return;
			setStep(1);
			return;
		}
		if (step === 1) {
			try {
				if (mode === 'new') {
					await form.validateFields(['first_name', 'last_name', 'email', 'ihi_number']);
				} else {
					await form.validateFields(['existing_patient_id']);
				}
				setStep(2);
			} catch {
				// validation errors shown inline
			}
		}
	};

	const goBack = () => {
		if (patientCreated) return;
		setStep((s) => Math.max(0, s - 1));
	};

	const sendLoginDetailsNotification = (created: Patient, password: string) => {
		if (!sendLoginDetails) return;
		if (ENV.VITE_NOTIFICATIONS_ENABLED !== 'true') return;

		const notificationPayload: SendCommunicationRequest = {
			recipient_user_id: created.id,
			category: 'new_patient_notification',
			user_id: user.id,
			channels: ['email', 'sms'],
			data: { ...created, password },
		};

		sendCommunication(notificationPayload).catch(() => {
			// No response body, status or credential is carried into the message.
			showWarning(LOGIN_DETAILS_NOT_SENT_TITLE, LOGIN_DETAILS_NOT_SENT_MESSAGE);
		});
	};

	const handleSubmit = async () => {
		const ihiCandidate =
			mode === 'new' && typeof form.getFieldValue('ihi_number') === 'string'
				? (form.getFieldValue('ihi_number') as string)
				: '';

		if (!createdPatientRef.current && ihiCandidate !== '' && !validateIHI(ihiCandidate).isValid) {
			setStep(1);
			return;
		}

		setSubmitting(true);
		try {
			let patientId: UserUuid;

			if (mode === 'new' && createdPatientRef.current) {
				patientId = createdPatientRef.current.id;
			} else if (mode === 'new') {
				const values = form.getFieldsValue();
				const password = generatePassword();
				const patientData: Partial<Patient> = {
					first_name:            values.first_name,
					last_name:             values.last_name,
					email:                 values.email,
					phone:                 values.phone ?? '',
					date_of_birth:         values.date_of_birth ? values.date_of_birth.toDate() : null,
					gender:                values.gender ?? '',
					sex:                   values.gender ?? '',
					is_active:             true,
					password,
					password_confirmation: password,
					address:               '',
					city:                  '',
					state:                 '',
					country:               '',
					postal_code:           '',
					profile_picture:       '',
					preferences:           '',
					panel_key:             '',
					reagents:              '',
					height:                '',
					weight:                '',
					roles:                 [],
					username:              values.email,
				};

				if (ihiCandidate !== '') {
					patientData.patient_info = { ihi_number: Number(ihiCandidate) };
				}

				const { ihi_validation, ...created } = await createPatient(patientData);
				createdPatientRef.current = created;
				setPatientCreated(true);

				sendLoginDetailsNotification(created, password);

				const ihiValidation = parseIhiValidationFeedback(ihi_validation);
				if (ihiValidation) {
					const serverIhi = created.patient_info?.ihi_number;
					form.setFieldsValue({
						ihi_number: serverIhi === undefined || serverIhi === null ? '' : String(serverIhi),
					});
					if (ihiValidation.candidate_discarded) {
						showWarning(IHI_NOT_SAVED_TITLE, ihiValidation.message);
					} else {
						showSuccess(IHI_SAVED_TITLE, ihiValidation.message);
					}
				}

				patientId = created.id;
			} else {
				patientId = form.getFieldValue('existing_patient_id');
			}

			await assignPlan({
				patient_id:                 patientId,
				treatment_plan_template_id: selectedTemplate!.id,
				practitioner_id:            user.id,
			});

			createdPatientRef.current = null;
			setPatientCreated(false);

			showSuccess(TREATMENT_PLAN_CREATED_MESSAGE);
			onCreated();
			onClose();
		} catch (err: any) {
			const refusedIhiValidation = createdPatientRef.current
				? null
				: extractIhiValidationFromMutationError(err);
			if (refusedIhiValidation?.candidate_discarded) {
				form.setFieldsValue({ ihi_number: '' });
				showWarning(PATIENT_NOT_SAVED_TITLE, refusedIhiValidation.message);
				return;
			}

			if (
				ihiCandidate !== ''
				&& !createdPatientRef.current
				&& err?.response?.status === 422
				&& refusedIhiValidation === null
				&& responseCarriesIhiValidation(err)
			) {
				showWarning(PATIENT_NOT_SAVED_TITLE, PATIENT_NOT_SAVED_UNCONFIRMED_MESSAGE);
				return;
			}

			const msg =
				err?.response?.data?.message ||
				err?.response?.data?.error ||
				'Failed to create treatment plan';
			showError(msg);
		} finally {
			setSubmitting(false);
		}
	};

	const footer = (
		<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
			{/* CU-14ygtkj65jy — after creation there is no Back: the only step it could
			    return to is the patient form, whose edits this modal cannot persist. */}
			<Button
				icon={patientCreated ? undefined : <ArrowLeftOutlined />}
				onClick={step === 0 || patientCreated ? onClose : goBack}
				data-testid="treatment-plan-modal-back-btn"
			>
				{patientCreated ? 'Close' : step === 0 ? 'Cancel' : 'Back'}
			</Button>
			{step < 2 ? (
				<Button
					type="primary"
					icon={<ArrowRightOutlined />}
					iconPosition="end"
					onClick={goNext}
					disabled={step === 0 && !selectedTemplate}
					data-testid="treatment-plan-modal-next-btn"
				>
					Next
				</Button>
			) : (
				<Button type="primary" loading={submitting} onClick={handleSubmit} icon={<CheckCircleOutlined />} data-testid="treatment-plan-modal-submit-btn">
					{patientCreated ? 'Assign Treatment Plan' : 'Create Treatment Plan'}
				</Button>
			)}
		</div>
	);

	const ConfirmStep: React.FC = () => {
		const values = form.getFieldsValue();
		const accent = CATEGORY_COLORS[selectedTemplate?.category ?? ''] ?? '#7c3aed';
		const patientName = mode === 'new'
			? `${values.first_name ?? ''} ${values.last_name ?? ''}`.trim()
			: selectedExistingPatient?.name ?? 'Selected patient';
		const patientEmail = mode === 'new' ? values.email : selectedExistingPatient?.email;

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				<div
					style={{
						padding: '16px 20px',
						borderRadius: 10,
						border: `1.5px solid ${accent}55`,
						background: `${accent}08`,
					}}
				>
					<div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
						<div
							style={{
								width: 40, height: 40, borderRadius: 10,
								background: `${accent}20`, display: 'flex', alignItems: 'center',
								justifyContent: 'center', fontSize: 18, color: accent, flexShrink: 0,
							}}
						>
							📋
						</div>
						<div style={{ flex: 1 }}>
							<Title level={5} style={{ margin: 0, fontSize: 14 }}>{selectedTemplate?.name}</Title>
							{selectedTemplate?.category && (
								<Tag
									style={{
										marginTop: 4, background: `${accent}18`, color: accent,
										border: `1px solid ${accent}44`, borderRadius: 20,
										fontSize: 10, fontWeight: 600, padding: '0 8px',
									}}
								>
									{selectedTemplate.category}
								</Tag>
							)}
							{selectedTemplate?.description && (
								<Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
									{selectedTemplate.description}
								</Text>
							)}
						</div>
					</div>
				</div>

				<div
					style={{
						padding: '14px 20px',
						borderRadius: 10,
						border: '1.5px solid var(--mg-border)',
						background: 'var(--mg-bg-elevated)',
						display: 'flex',
						alignItems: 'center',
						gap: 12,
					}}
				>
					<div
						style={{
							width: 36, height: 36, borderRadius: '50%',
							background: '#e0e7ff', display: 'flex', alignItems: 'center',
							justifyContent: 'center', fontSize: 16, color: '#6366f1', flexShrink: 0,
						}}
					>
						<UserOutlined />
					</div>
					<div>
						<Text strong style={{ fontSize: 13 }}>{patientName || '—'}</Text>
						{patientEmail && (
							<Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{patientEmail}</Text>
						)}
						{mode === 'new' && (
							<Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
								{patientCreated
									? 'New patient — account created'
									: 'New patient — account will be created automatically'}
							</Text>
						)}
					</div>
				</div>

				{mode === 'new' && patientCreated && (
					<Alert
						type="info"
						showIcon
						message={PATIENT_ALREADY_CREATED_TITLE}
						description={PATIENT_ALREADY_CREATED_MESSAGE}
						data-testid="treatment-plan-patient-already-created"
					/>
				)}

				{mode === 'new' && !patientCreated && (
					<div>
						<Checkbox
							checked={sendLoginDetails}
							onChange={(e) => setSendLoginDetails(e.target.checked)}
							data-testid="treatment-plan-send-login-details"
						>
							<Text style={{ fontSize: 13 }}>{LOGIN_DETAILS_SEND_LABEL}</Text>
						</Checkbox>
						<Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
							{sendLoginDetails
								? 'The patient will receive their username and a temporary password. They can reset it via the login page.'
								: 'Nothing will be sent. The patient will need the reset-password link on the login page before they can sign in.'}
						</Text>
					</div>
				)}
			</div>
		);
	};

	const handleImportComplete = async (job: XmlImportJobStatus) => {
		setImportModalOpen(false);
		if (job.patient_uuid) {
			try {
				const { data: patient } = await fetchSinglePatientByUuid(job.patient_uuid);
				setMode('existing');
				setSelectedExistingPatient({
					id: patient.id,
					name: `${patient.first_name} ${patient.last_name}`.trim(),
					email: patient.email,
				});
				form.setFieldValue('existing_patient_id', patient.id);
				setStep(2);
			} catch {
				// fall back to closing if patient lookup fails
				onClose();
			}
		} else {
			onClose();
		}
	};

	return (
		<>
		<Modal
			open={open}
			title={
				<Space>
					<PlusCircleOutlined style={{ color: '#7c3aed' }} />
					<span>New Treatment Plan</span>
				</Space>
			}
			footer={footer}
			onCancel={onClose}
			width={720}
			destroyOnClose
			data-testid="treatment-plan-create-modal"
		>
			<BetaNotice compact />
			<Steps
				current={step}
				size="small"
				style={{ margin: '12px 0 24px' }}
				items={[
					{ title: 'Select Pathway' },
					{ title: 'Patient Details' },
					{ title: 'Confirm' },
				]}
			/>

			{step === 0 && (
				<div>
					{loadingTemplates ? (
						<div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--mg-text-secondary)' }}>
							Loading pathways…
						</div>
					) : templates.length === 0 ? (
						<div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--mg-text-secondary)' }}>
							No active treatment plan templates found.
						</div>
					) : (
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
								gap: 12,
								maxHeight: 380,
								overflowY: 'auto',
								paddingRight: 4,
							}}
						>
							{templates.map((t) => (
								<TemplateCard
									key={t.id}
									template={t}
									selected={selectedTemplate?.id === t.id}
									onSelect={() => setSelectedTemplate(t)}
								/>
							))}
						</div>
					)}
				</div>
			)}

			{step === 1 && (
				<Form form={form} layout="vertical" size="middle">
					<Form.Item style={{ marginBottom: 16 }}>
						<Segmented
							value={mode}
							onChange={handleModeChange}
							options={[
								{ label: 'New Patient', value: 'new' },
								{ label: 'Existing Patient', value: 'existing' },
							]}
							block
						/>
					</Form.Item>

				{mode === 'new' && (
					<>
						<Form.Item style={{ marginBottom: 12 }}>
							<Button
								icon={<FileSyncOutlined />}
								block
								onClick={() => setImportModalOpen(true)}
							>
								Import Patient Record
							</Button>
						</Form.Item>
						<div style={{ display: 'flex', gap: 12 }}>
							<Form.Item
								name="first_name"
								label="First Name"
								rules={[{ required: true, message: 'Required' }]}
								style={{ flex: 1, marginBottom: 12 }}
							>
								<Input placeholder="Jane" autoFocus />
							</Form.Item>
							<Form.Item
									name="last_name"
									label="Last Name"
									rules={[{ required: true, message: 'Required' }]}
									style={{ flex: 1, marginBottom: 12 }}
								>
									<Input placeholder="Smith" />
								</Form.Item>
							</div>
							<Form.Item
								name="email"
								label="Email"
								rules={[
									{ required: true, message: 'Required' },
									{ type: 'email', message: 'Enter a valid email' },
								]}
								style={{ marginBottom: 12 }}
							>
								<Input placeholder="jane.smith@example.com" />
							</Form.Item>
							<Form.Item name="phone" label="Phone" style={{ marginBottom: 12 }}>
								<Input placeholder="+61 4XX XXX XXX" />
							</Form.Item>
							<div style={{ display: 'flex', gap: 12 }}>
								<Form.Item name="date_of_birth" label="Date of Birth" style={{ flex: 1, marginBottom: 0 }}>
									<DatePicker
										style={{ width: '100%' }}
										format="DD/MM/YYYY"
										disabledDate={(d) => d && d.isAfter(dayjs())}
										placeholder="DD/MM/YYYY"
									/>
								</Form.Item>
								<Form.Item name="gender" label="Gender" style={{ flex: 1, marginBottom: 0 }}>
									<Select options={GENDER_OPTIONS} placeholder="Select…" allowClear />
								</Form.Item>
							</div>
							<Form.Item
								name="ihi_number"
								label="IHI Number"
								rules={[{ validator: validateIhiCandidate }]}
								style={{ marginBottom: 0, marginTop: 12 }}
								extra="Optional. Leave blank if the patient's IHI is not known."
							>
								<Input
									placeholder="Enter IHI number"
									data-testid="treatment-plan-patient-ihi"
									inputMode="numeric"
									autoComplete="off"
								/>
							</Form.Item>
						</>
					)}

					{mode === 'existing' && (
						<Form.Item
							name="existing_patient_id"
							label="Search Patient"
							rules={[{ required: true, message: 'Please select a patient' }]}
						>
							<Select
								showSearch
								filterOption={false}
								onSearch={handlePatientSearch}
								loading={searchingPatients}
								placeholder="Type a name, email or phone…"
								notFoundContent={searchingPatients ? 'Searching…' : 'No patients found'}
								options={patientOptions}
								onChange={(val) => {
									const opt = patientOptions.find((o) => o.value === val);
									setSelectedExistingPatient(
										opt ? { id: val, name: opt.label.split(' — ')[0], email: opt.email } : null
									);
								}}
							/>
						</Form.Item>
					)}
				</Form>
			)}

			{step === 2 && <ConfirmStep />}
		</Modal>

		<MedicalDirectorImportModal
			open={importModalOpen}
			onClose={() => setImportModalOpen(false)}
			mode="new_patient"
			onImportComplete={handleImportComplete}
		/>
		</>
	);
};

export default CreateTreatmentPlanModal;